import type { DateValue } from "@internationalized/date";
import type { Matcher } from "reka-ui/date";
import { CalendarDate } from "@internationalized/date";
import {
  CalendarCell,
  CalendarCellTrigger,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHead,
  CalendarGridRow,
  CalendarHeadCell,
  CalendarHeader,
  CalendarNext,
  CalendarPrev,
  CalendarRoot,
} from "reka-ui";
import { defineComponent, shallowRef } from "vue";
import Icon from "@/components/ui/icon/Icon";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";

function getMonthNames(locale: string): string[] {
  const formatter = new Intl.DateTimeFormat(locale, { month: "long" });
  return Array.from({ length: 12 }, (_, i) => formatter.format(new Date(2024, i, 1)));
}

const YEAR_RANGE = 20;

const Root = defineComponent({
  name: "Calendar",
  props: {
    modelValue: p.custom<DateValue | DateValue[] | undefined>([Object, Array]),
    defaultValue: p.custom<DateValue | undefined>([Object]),
    placeholder: p.custom<DateValue | undefined>([Object]),
    defaultPlaceholder: p.custom<DateValue | undefined>([Object]),
    locale: p.string("en-US"),
    weekStartsOn: p.number<0 | 1 | 2 | 3 | 4 | 5 | 6>(0),
    weekdayFormat: p.string<"narrow" | "short" | "long">("narrow"),
    fixedWeeks: p.boolean(false),
    numberOfMonths: p.number(1),
    disabled: p.boolean(false),
    readonly: p.boolean(false),
    initialFocus: p.boolean(false),
    pagedNavigation: p.boolean(false),
    preventDeselect: p.boolean(false),
    isDateDisabled: p.func<Matcher>(),
    isDateUnavailable: p.func<Matcher>(),
    minValue: p.custom<DateValue | undefined>([Object]),
    maxValue: p.custom<DateValue | undefined>([Object]),
    multiple: p.boolean(false),
    disableDaysOutsideCurrentView: p.boolean(false),
  },
  emits: {
    "update:modelValue": (_date: DateValue | undefined) => true,
    "update:placeholder": (_date: DateValue) => true,
  },
  setup(props, { emit, attrs }) {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: YEAR_RANGE * 2 + 1 }, (_, i) => currentYear - YEAR_RANGE + i);

    const internalPlaceholder = shallowRef<DateValue>(
      props.placeholder ?? props.defaultPlaceholder ?? new CalendarDate(currentYear, new Date().getMonth() + 1, 1),
    );

    function handlePlaceholderUpdate(date: DateValue) {
      internalPlaceholder.value = date;
      emit("update:placeholder", date);
    }

    function handleMonthChange(month: number) {
      internalPlaceholder.value = new CalendarDate(internalPlaceholder.value.year, month, 1);
    }

    function handleYearChange(year: number) {
      internalPlaceholder.value = new CalendarDate(year, internalPlaceholder.value.month, 1);
    }

    return () => {
      const monthNames = getMonthNames(props.locale);
      const selectClass = "text-sm font-medium capitalize appearance-none border-0 bg-transparent cursor-pointer outline-none focus:ring-0 p-0 pr-0 hover:text-accent-11 transition-colors";
      const selectStyle = { backgroundImage: "none" };

      return (
        <CalendarRoot
          class={cn("p-3", attrs.class as string)}
          modelValue={props.modelValue}
          defaultValue={props.defaultValue}
          placeholder={internalPlaceholder.value}
          defaultPlaceholder={props.defaultPlaceholder}
          locale={props.locale}
          weekStartsOn={props.weekStartsOn}
          weekdayFormat={props.weekdayFormat}
          fixedWeeks={props.fixedWeeks}
          numberOfMonths={props.numberOfMonths}
          disabled={props.disabled}
          readonly={props.readonly}
          initialFocus={props.initialFocus}
          pagedNavigation={props.pagedNavigation}
          preventDeselect={props.preventDeselect}
          isDateDisabled={props.isDateDisabled}
          isDateUnavailable={props.isDateUnavailable}
          minValue={props.minValue}
          maxValue={props.maxValue}
          multiple={props.multiple}
          disableDaysOutsideCurrentView={props.disableDaysOutsideCurrentView}
          onUpdate:modelValue={(date: DateValue | undefined) => emit("update:modelValue", date)}
          onUpdate:placeholder={handlePlaceholderUpdate}
          v-slots={{
            default: ({
              grid,
              weekDays,
            }: {
              grid: { value: DateValue; rows: DateValue[][] }[];
              weekDays: string[];
            }) => (
              <div class="flex flex-col gap-4 sm:flex-row">
                {grid.map((month, idx) => (
                  <div key={idx} class="flex flex-col gap-4">
                    <CalendarHeader class="relative flex w-full items-center justify-between gap-1">
                      <CalendarPrev class="text-muted-foreground hover:bg-muted hover:text-foreground inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors disabled:pointer-events-none disabled:opacity-50">
                        <Icon icon="lucide:chevron-left" class="h-4 w-4" />
                      </CalendarPrev>

                      <div class="flex items-center gap-1">
                        <select
                          class={selectClass}
                          style={selectStyle}
                          value={month.value.month}
                          onChange={(e: Event) => handleMonthChange(Number((e.target as HTMLSelectElement).value))}
                        >
                          {monthNames.map((name, i) => (
                            <option key={i} value={i + 1}>{name}</option>
                          ))}
                        </select>
                        <select
                          class={selectClass}
                          style={selectStyle}
                          value={month.value.year}
                          onChange={(e: Event) => handleYearChange(Number((e.target as HTMLSelectElement).value))}
                        >
                          {years.map((y) => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                      </div>

                      <CalendarNext class="text-muted-foreground hover:bg-muted hover:text-foreground inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors disabled:pointer-events-none disabled:opacity-50">
                        <Icon icon="lucide:chevron-right" class="h-4 w-4" />
                      </CalendarNext>
                    </CalendarHeader>

                    <CalendarGrid class="w-full border-collapse">
                      <CalendarGridHead>
                        <CalendarGridRow class="flex">
                          {weekDays.map((day) => (
                            <CalendarHeadCell
                              key={day}
                              class="text-muted-foreground w-8 rounded-md text-center text-[0.8rem] font-normal"
                            >
                              {day}
                            </CalendarHeadCell>
                          ))}
                        </CalendarGridRow>
                      </CalendarGridHead>
                      <CalendarGridBody>
                        {month.rows.map((week, weekIdx) => (
                          <CalendarGridRow key={weekIdx} class="mt-2 flex w-full">
                            {week.map((day) => (
                              <CalendarCell
                                key={day.toString()}
                                date={day}
                                class="relative p-0 text-center text-sm"
                              >
                                <CalendarCellTrigger
                                  day={day}
                                  month={month.value}
                                  class={cn(
                                    "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm tabular-nums transition-colors",
                                    "focus-visible:ring-ring focus-visible:ring-1 focus-visible:outline-none",
                                    "hover:bg-muted hover:text-foreground",
                                    "data-selected:bg-accent-9 data-selected:text-accent-contrast data-selected:hover:bg-accent-10 data-selected:hover:text-accent-contrast data-selected:focus-visible:ring-accent-9",
                                    "data-today:bg-accent-3 data-today:text-accent-11 data-today:font-medium",
                                    "data-disabled:text-muted-foreground data-disabled:pointer-events-none data-disabled:opacity-50",
                                    "data-outside-view:text-muted-foreground/50 data-outside-view:pointer-events-none",
                                    "data-unavailable:text-danger-9 data-unavailable:line-through",
                                    "data-selected:data-today:bg-accent-9 data-selected:data-today:text-accent-contrast",
                                  )}
                                />
                              </CalendarCell>
                            ))}
                          </CalendarGridRow>
                        ))}
                      </CalendarGridBody>
                    </CalendarGrid>
                  </div>
                ))}
              </div>
            ),
          }}
        />
      );
    };
  },
});

export default {
  Root,
};
